import { Injectable, NgZone, OnDestroy } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class MatSelectPanelScrollService implements OnDestroy {
  private observer?: MutationObserver;
  private initialized = false;
  private scrollFrame?: number;
  private scrollListenerCleanup?: () => void;
  private readonly panelViewportGap = 8;
  private readonly panelMinHeight = 96;
  private readonly panelMaxHeight = 330;

  constructor(private zone: NgZone) {}

  start(): void {
    if (this.initialized || typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    this.initialized = true;

    this.zone.runOutsideAngular(() => {
      this.observer = new MutationObserver(mutations => {
        for (const mutation of mutations) {
          mutation.addedNodes.forEach(node => this.handleNode(node));
        }
      });

      this.observer.observe(document.body, {
        childList: true,
        subtree: true,
      });

      window.addEventListener('scroll', this.scheduleOpenPanelAlignment, { capture: true, passive: true });
      window.addEventListener('resize', this.scheduleOpenPanelAlignment, { passive: true });
      this.scrollListenerCleanup = () => {
        window.removeEventListener('scroll', this.scheduleOpenPanelAlignment, { capture: true } as EventListenerOptions);
        window.removeEventListener('resize', this.scheduleOpenPanelAlignment);
      };
    });
  }

  ngOnDestroy(): void {
    this.stop();
  }

  stop(): void {
    this.observer?.disconnect();
    this.observer = undefined;
    this.scrollListenerCleanup?.();
    this.scrollListenerCleanup = undefined;
    if (this.scrollFrame !== undefined) {
      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = undefined;
    }
    this.initialized = false;
  }

  private readonly scheduleOpenPanelAlignment = (): void => {
    if (this.scrollFrame !== undefined) {
      return;
    }

    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = undefined;
      this.alignOpenPanels();
    });
  };

  private alignOpenPanels(): void {
    Array.from(document.querySelectorAll<HTMLElement>('.mat-mdc-select-panel, .mat-select-panel'))
      .forEach(panel => this.applyPanelAlignment(panel));
  }

  private handleNode(node: Node): void {
    if (!(node instanceof HTMLElement)) {
      return;
    }

    const panels = node.matches('.mat-mdc-select-panel, .mat-select-panel')
      ? [node]
      : Array.from(node.querySelectorAll<HTMLElement>('.mat-mdc-select-panel, .mat-select-panel'));

    panels.forEach(panel => {
      this.alignPanelToTrigger(panel);

      if (this.isSearchPanel(panel)) {
        this.scrollPanelToTop(panel);
      }
    });
  }

  private alignPanelToTrigger(panel: HTMLElement): void {
    requestAnimationFrame(() => {
      this.applyPanelAlignment(panel);
      requestAnimationFrame(() => this.applyPanelAlignment(panel));
    });
  }

  private applyPanelAlignment(panel: HTMLElement): void {
    if (!document.body.contains(panel)) {
      return;
    }

    const trigger = this.findPanelTrigger(panel);
    const triggerContainer = trigger?.closest<HTMLElement>('.mat-mdc-form-field, .mat-form-field') ?? trigger;
    if (!triggerContainer) {
      return;
    }

    const triggerRect = triggerContainer.getBoundingClientRect();

    const openAbove = this.shouldOpenAbove(panel, triggerRect);
    const maxHeight = this.getResponsiveMaxHeight();
    const overlayPane = panel.closest<HTMLElement>('.cdk-overlay-pane');

    panel.style.setProperty('position', 'relative', 'important');
    panel.style.setProperty('top', '0px', 'important');
    panel.style.setProperty('max-height', `${maxHeight}px`, 'important');
    overlayPane?.style.setProperty('height', 'auto', 'important');
    overlayPane?.style.setProperty('max-height', `${maxHeight}px`, 'important');

    const panelRect = panel.getBoundingClientRect();

    if (!panelRect.width || !triggerRect.width) {
      return;
    }

    if (openAbove) {
      panel.style.setProperty('top', `${Math.round(triggerRect.top - panelRect.bottom)}px`, 'important');
      overlayPane?.classList.add('mat-mdc-select-panel-above');
      return;
    }

    panel.style.setProperty('top', `${Math.round(triggerRect.top - panelRect.top)}px`, 'important');
    overlayPane?.classList.remove('mat-mdc-select-panel-above');
  }

  private findPanelTrigger(panel: HTMLElement): HTMLElement | null {
    if (panel.id) {
      const trigger = Array.from(document.querySelectorAll<HTMLElement>('[aria-controls], [aria-owns]')).find(element =>
        element.getAttribute('aria-controls') === panel.id || element.getAttribute('aria-owns') === panel.id
      );

      if (trigger) {
        return trigger;
      }

      const triggerId = panel.id.replace(/-panel$/, '');
      const triggerById = document.getElementById(triggerId);
      if (triggerById instanceof HTMLElement) {
        return triggerById;
      }
    }

    const activeElement = document.activeElement;
    return activeElement instanceof HTMLElement
      ? activeElement.closest<HTMLElement>('mat-select, .mat-mdc-select, .mat-select')
      : null;
  }

  private getResponsiveMaxHeight(): number {
    return Math.max(
      this.panelMinHeight,
      Math.min(this.panelMaxHeight, window.innerHeight - this.panelViewportGap * 2)
    );
  }

  private shouldOpenAbove(panel: HTMLElement, triggerRect: DOMRect): boolean {
    const availableBelow = this.getAvailableHeightBelow(triggerRect);
    const availableAbove = this.getAvailableHeightAbove(triggerRect);
    const desiredPanelHeight = this.getDesiredPanelHeight(panel);

    return availableBelow < desiredPanelHeight && availableAbove > availableBelow;
  }

  private getDesiredPanelHeight(panel: HTMLElement): number {
    return Math.min(
      this.panelMaxHeight,
      panel.scrollHeight || panel.getBoundingClientRect().height || this.panelMaxHeight
    );
  }

  private getAvailableHeightBelow(triggerRect: DOMRect): number {
    return window.innerHeight - triggerRect.bottom - this.panelViewportGap;
  }

  private getAvailableHeightAbove(triggerRect: DOMRect): number {
    return triggerRect.top - this.panelViewportGap;
  }

  private isSearchPanel(panel: HTMLElement): boolean {
    const classNames = ['organization-select', 'stock-room-select', 'header-search', 'drp-ucd', 'select-vendor'];
    if (classNames.some(className => panel.classList.contains(className))) {
      return true;
    }

    return Boolean(panel.querySelector('input[type="text"], input[matinput], mat-form-field'));
  }

  private scrollPanelToTop(panel: HTMLElement): void {
    const reset = () => {
      panel.scrollTop = 0;
      panel.scrollTo?.({ top: 0, behavior: 'auto' });
    };

    reset();
    requestAnimationFrame(reset);
  }
}
